<script setup>
import { useEventListener, useTimeoutFn } from '@vueuse/core'
import { ref, toRef, useTemplateRef, computed } from 'vue'

const props = defineProps(['link', 'scale'])
defineExpose({ print })
const scale = toRef(props, 'scale')
const emit = defineEmits(['loaded'])
const frameRef = useTemplateRef('frame-ref')
const frameWin = computed(() => frameRef.value?.contentWindow)
const iframeHeight = ref()
const iframeWidth = ref()
const scaleCss = computed(() => `scale(${scale.value})`)
// timeout here to wait iframe body full loaded to have dimension filled
const { start: iframeLoadStart } = useTimeoutFn(iframeLoaded, 150)
let printResolve
const inch2PxRegex = /([0-9.]+)in\s+([0-9.]+)in/im
const pixelSize = 96 // pixels in 1 inch
useEventListener(frameWin, 'afterprint', () => {
  printResolve()
  printResolve = undefined
})
function iframeLoaded() {
  const doc = frameRef.value.contentDocument
  let w
  let ml, mr, mt, mb

  /*
   * 1: check @page rule style
   */
  for (const styleSheet of doc.styleSheets) {
    if (!styleSheet.rules.length) {
      continue
    }
    for (const rule of styleSheet.rules) {
      if (rule.type != 6) {
        continue
      }
      ;(() => {
        let size = rule.style.getPropertyValue('size')
        if (!size) {
          return
        }
        let sizeMatch = size.match(inch2PxRegex)
        if (!sizeMatch?.length) {
          return
        }
        ;[w] = [sizeMatch[1], sizeMatch[2]].map(parseFloat).map(n => n * pixelSize)
      })()
      ;(() => {
        ;[mt, mr, mb, ml] = ['top', 'right', 'bottom', 'left'].map(s =>
          rule.style.getPropertyValue(`margin-${s}`)
        )
        if (!mt) {
          return
        }
        doc.body.style.margin = `${mt} ${mr} ${mb} ${ml}`
      })()
    }
  }
  w = doc?.body.scrollWidth

  iframeWidth.value = w + 'px'
  iframeHeight.value = doc.body.scrollHeight + 'px'

  emit('loaded', null)
}
async function print() {
  return new Promise(ok => {
    printResolve = ok
    frameRef.value.focus()
    frameWin.value.print()
  })
}
</script>
<template>
  <iframe
    :src="link"
    @load="iframeLoadStart"
    width="100%"
    height="100%"
    ref="frame-ref"
    loading="eager"
    scrolling="no"
  />
</template>
<style scoped>
iframe {
  height: v-bind('iframeHeight');
  transform: v-bind('scaleCss');
  transform-origin: top left;
  min-width: v-bind('iframeWidth');
}
</style>
